function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

import React, { useCallback } from "react";
import PropTypes from "prop-types";
import useTheme from "../../hooks/useTheme";
import useBackground from "../../hooks/useBackground";
import useResponsivePropsCSS from "../../hooks/useResponsivePropsCSS";
import { mergeProps } from "../../utils/component";
import { jsx as ___EmotionJSX } from "@emotion/core";
var TYPES = ["text", "password", "email", "tel"];
var VARIANTS = ["text", "numeric", "decimal"];
var COLORS = ["grey.t05", "white"];
var NUMERIC_REGEX = /^\d*$/;
var DECIMAL_REGEX = /^\d*(\.\d{2})?$/;
var DEFAULT_PROPS = {
  type: "text",
  variant: "text",
  color: "grey.t05",
  disabled: false,
  autoComplete: "off",
  pasteAllowed: true,
  isValid: true,
  __internal__focus: false
};
InternalInput.TYPES = TYPES;
InternalInput.VARIANTS = VARIANTS;
InternalInput.COLORS = COLORS;
InternalInput.NUMERIC_REGEX = NUMERIC_REGEX;
InternalInput.DECIMAL_REGEX = DECIMAL_REGEX;
InternalInput.DEFAULT_PROPS = DEFAULT_PROPS;

function InternalInput(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    type: function type(_type) {
      return TYPES.includes(_type);
    },
    variant: function variant(_variant) {
      return VARIANTS.includes(_variant);
    },
    prefix: function prefix(_prefix) {
      return typeof _prefix === "string" && _prefix.length > 0;
    },
    suffix: function suffix(_suffix) {
      return typeof _suffix === "string" && _suffix.length > 0;
    },
    maxLength: function maxLength(_maxLength) {
      return typeof _maxLength === "string" || typeof _maxLength === "number";
    },
    autoComplete: function autoComplete(_autoComplete) {
      return typeof _autoComplete === "string";
    },
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    pasteAllowed: function pasteAllowed(_pasteAllowed) {
      return typeof _pasteAllowed === "boolean";
    }
  });
  var name = mergedProps.name,
      parentName = mergedProps.parentName,
      id = mergedProps.id,
      type = mergedProps.type,
      placeholder = mergedProps.placeholder,
      variant = mergedProps.variant,
      prefix = mergedProps.prefix,
      suffix = mergedProps.suffix,
      maxLength = mergedProps.maxLength,
      autoComplete = mergedProps.autoComplete,
      disabled = mergedProps.disabled,
      pasteAllowed = mergedProps.pasteAllowed,
      isValid = mergedProps.isValid,
      describedBy = mergedProps.describedBy,
      onFocus = mergedProps.onFocus,
      onBlur = mergedProps.onBlur,
      value = mergedProps.value,
      onChange = mergedProps.onChange,
      __internal__focus = mergedProps.__internal__focus;
  var theme = useTheme();

  var _useBackground = useBackground(),
      inputColorMap = _useBackground.inputColorMap;

  var inputCSS = useResponsivePropsCSS(props, DEFAULT_PROPS, {
    color: function color(propsAtBreakpoint, theme, bp) {
      var _props$color;

      var color = (_props$color = props.color) !== null && _props$color !== void 0 ? _props$color : inputColorMap[bp];
      return theme.input.getCSS({
        targetElement: "input",
        variant: variant,
        prefix: prefix,
        suffix: suffix,
        color: color,
        __internal__focus: __internal__focus
      });
    }
  });
  var onPaste = useCallback(function (event) {
    if (!pasteAllowed) {
      event.preventDefault();
    }
  }, [pasteAllowed]);
  var variantProps = variant === "numeric" ? {
    // See: https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers
    inputMode: "numeric",
    pattern: "[0-9]*"
  } : variant === "decimal" ? {
    inputMode: "decimal"
  } : {};
  return ___EmotionJSX("div", {
    css: theme.input.getCSS({
      targetElement: "inputContainer",
      variant: variant,
      prefix: prefix,
      suffix: suffix
    })
  }, ___EmotionJSX("input", _extends({
    css: inputCSS,
    id: id,
    name: name,
    "data-parent-name": parentName,
    placeholder: placeholder,
    type: type
  }, variantProps, {
    maxLength: maxLength,
    disabled: disabled,
    onPaste: onPaste,
    autoComplete: autoComplete,
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: "false",
    "aria-invalid": isValid ? null : "true",
    "aria-describedby": describedBy,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value,
    onChange: onChange
  })));
}

InternalInput.propTypes = {
  name: PropTypes.string.isRequired,
  parentName: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.oneOf(TYPES),
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(VARIANTS),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoComplete: PropTypes.string,
  color: PropTypes.oneOf(COLORS),
  disabled: PropTypes.bool,
  pasteAllowed: PropTypes.bool,
  isValid: PropTypes.bool,
  describedBy: PropTypes.string,
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  __internal__focus: PropTypes.bool
};
export default InternalInput;